import { AbstractControl, FormGroup } from "@angular/forms";
import { ValidationErrorFieldsFragment } from "./graphql/gql";
import { take } from "rxjs";

export class AppValidationHelpers {
  /**
   * Applies server-side validation errors to the matching form controls.
   *
   * An error whose `path` names no control in the form cannot be rendered next to a field. Those
   * are returned rather than dropped, so the caller can surface them some other way — otherwise
   * the submission fails with nothing at all shown to the user, which is what happened for the
   * `clientNumber` error in BNDDEV-10062.
   *
   * @returns the errors which could not be applied to a control
   */
  static applyValidationErrors(
    validationErrors: ValidationErrorFieldsFragment[],
    form: FormGroup
  ): ValidationErrorFieldsFragment[] {
    const unmapped: ValidationErrorFieldsFragment[] = [];

    validationErrors.forEach(validationError => {
      const path = validationError.path;
      const control = path ? form.get(path) : form;
      if (control) {
        control.setErrors({ [validationError.type]: { remoteMessage: validationError.message } });
        // Clear remote error when user edits so the control can become valid again
        control.valueChanges.pipe(take(1)).subscribe(() => {
          AppValidationHelpers.clearRemoteErrors(control);
        });
      } else {
        unmapped.push(validationError);
      }
    });

    return unmapped;
  }

  /**
   * Formats the validation errors which could not be attached to a form control, for display on
   * their own — in a toast, say.
   *
   * Prefers `fullMessage`, which names the attribute ("Client number has already been taken"),
   * because without a field label beside it the bare message says nothing about what went wrong.
   * Falls back to `message` so an older schema still produces something readable.
   * See BNDDEV-10062.
   *
   * @returns the message to display, or null when there is nothing to report
   */
  static unmappedErrorMessage(unmapped: ValidationErrorFieldsFragment[]): string | null {
    if (unmapped.length === 0) {
      return null;
    }

    return unmapped.map(error => error.fullMessage || error.message).join(' ');
  }

  /** Removes server-applied validation errors and re-runs validators. */
  static clearRemoteErrors(control: AbstractControl): void {
    if (!control.errors) return;
    const withoutRemote = Object.fromEntries(
      Object.entries(control.errors).filter(
        ([_, v]) => !(typeof v === 'object' && v !== null && 'remoteMessage' in v)
      )
    );
    control.setErrors(Object.keys(withoutRemote).length ? withoutRemote : null);
    control.updateValueAndValidity({ onlySelf: true });
  }

  static getRemoteValidationMessages(control: AbstractControl | null): string[] {
    if (!control?.errors) {
      return [];
    }
    return Object.values(control.errors)
      .filter(error => typeof error === 'object' && 'remoteMessage' in error)
      .map(error => error.remoteMessage);
  }
}